import React, { useEffect, useMemo, useState } from "react";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { db, auth } from "../firebase";
import { Timestamp, doc, getDoc, onSnapshot, updateDoc } from "firebase/firestore";
import "../styles/Modal.css";
import "../styles/Factures.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FirestoreDevis } from "../types/firestore";
import { CSVLink } from "react-csv";
import { FaEdit, FaTrash, FaFileExport, FaPaperclip, FaCopy } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import FactureModal from "../components/FactureModal";
import FactureFoodinnovModal from "../components/FactureFoodinnovModal";
import StatusEditModal from "../components/StatusEditModal";
import { cleanupInvoices } from '../utils/cleanupInvoices';
import { usePageTitle } from '../hooks/usePageTitle';
import BillingRecapTable from '../components/BillingRecapTable';
import { logUserAction } from '../utils/logUtils';
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { getPDFCoordinatesFromStorage } from "../utils/pdfCoordinates";
import {
  FACTURE_PDF_SCALE,
  FACTURE_FONT_SIZE_OFFSET,
  FACTURE_Y_OFFSET_CORRECTION,
  FACTURE_Y_OFFSET_PDF_GENERATION,
  FACTURE_X_OFFSET_PDF_GENERATION
} from "../constants/FactureConfig";
import FacturePreviewModal from '../components/FacturePreviewModal';

interface Invoice {
  id: string;
  companyId: string;
  opportunityType: string;
  qte: number;
  pu: number;
  montantHT: number;
  montantTTC: number;
  amineAmount: number;
  amineAmountTTC: number;
  pourcentage: number;
  date: Timestamp;
  updatedAt: Timestamp;
  isSigned: boolean;
  uid?: string;
  status?: string;
  invoiceNumber?: string;
  source?: 'foodinnov' | 'regexport';
  opportunityDate?: string;
}

const FacturesFoodinnov: React.FC = () => {
  usePageTitle("Factures Foodinnov");
  const location = useLocation();
  // États
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [preselectedDevis, setPreselectedDevis] = useState<FirestoreDevis | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth().toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedOpportunityType, setSelectedOpportunityType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [opportunityTypes, setOpportunityTypes] = useState<string[]>([]);

  // New state for confirmation modal
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);

  // États pour les opportunités par société
  const [foodinnovOpportunities, setFoodinnovOpportunities] = useState<{[key: string]: any[]}>({});
  const [regexportOpportunities, setRegexportOpportunities] = useState<{[key: string]: any[]}>({});

  // États pour le deuxième tableau (factures RegExport)
  const [regexportInvoices, setRegexportInvoices] = useState<Invoice[]>([]);
  
  // États pour la modale RegExport
  const [showRegexportModal, setShowRegexportModal] = useState(false);
  const [selectedRegexportInvoice, setSelectedRegexportInvoice] = useState<Invoice | null>(null);

  // États pour la modale d'aperçu PDF
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [invoiceToPreview, setInvoiceToPreview] = useState<Invoice | null>(null);
  const [invoicePdfPreviewUrl, setInvoicePdfPreviewUrl] = useState<string | null>(null);

  // États pour la modale de modification de statut
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [invoiceToEditStatus, setInvoiceToEditStatus] = useState<Invoice | null>(null);

  // États pour le tri
  const [sortField, setSortField] = useState<string>('none');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [sortField2, setSortField2] = useState<string>('none');
  const [sortDirection2, setSortDirection2] = useState<'asc' | 'desc'>('desc');

  // États pour l'édition inline du numéro de facture
  const [editingInvoiceNumber, setEditingInvoiceNumber] = useState<string | null>(null);
  const [editingInvoiceNumberValue, setEditingInvoiceNumberValue] = useState<string>("");

  const filteredInvoices = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    let filtered = invoices.filter(
      (invoice) => {
        const matchesSearch = invoice.companyId.toLowerCase().includes(lower) ||
          invoice.opportunityType.toLowerCase().includes(lower) ||
          invoice.id.toLowerCase().includes(lower);

        // Statut normalisé ("Pas Réglée" et vide = "En attente de Paiement")
        const effectiveStatus = invoice.status === 'Réglée' || invoice.status === 'Gelée'
          ? invoice.status
          : 'En attente de Paiement';
        const matchesStatus = selectedStatus === "all" || effectiveStatus === selectedStatus;

        // Vérifier que la date existe avant de l'utiliser
        if (!invoice.date) {
          // Si pas de date, ne pas filtrer par mois/année
          return matchesSearch && matchesStatus;
        }

        const invoiceDate = invoice.date.toDate();
        const matchesMonth = selectedMonth === "all" || invoiceDate.getMonth() === parseInt(selectedMonth);
        const matchesYear = selectedYear === "all" || invoiceDate.getFullYear() === parseInt(selectedYear);
        const matchesOpportunityType = selectedOpportunityType === "all" || invoice.opportunityType === selectedOpportunityType;

        return matchesSearch && matchesMonth && matchesYear && matchesOpportunityType && matchesStatus;
      }
    );

    // Appliquer le tri si nécessaire
    if (sortField !== 'none') {
      filtered.sort((a, b) => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        switch (sortField) {
          case 'date': {
            const da = a.date?.toMillis?.() ?? 0;
            const db = b.date?.toMillis?.() ?? 0;
            return (da - db) * dir;
          }
          case 'invoiceNumber': return ((a.invoiceNumber || '').localeCompare(b.invoiceNumber || '')) * dir;
          case 'companyId': return ((a.companyId || '').localeCompare(b.companyId || '')) * dir;
          case 'opportunityType': return ((a.opportunityType || '').localeCompare(b.opportunityType || '')) * dir;
          case 'qte': return ((a.qte ?? 0) - (b.qte ?? 0)) * dir;
          case 'pu': return ((a.pu ?? 0) - (b.pu ?? 0)) * dir;
          case 'montantHT': return ((a.montantHT ?? 0) - (b.montantHT ?? 0)) * dir;
          case 'montantTTC': return ((a.montantTTC ?? 0) - (b.montantTTC ?? 0)) * dir;
          case 'pourcentage': return ((a.pourcentage ?? 0) - (b.pourcentage ?? 0)) * dir;
          case 'amineAmount': return ((a.amineAmount ?? 0) - (b.amineAmount ?? 0)) * dir;
          case 'amineAmountTTC': return ((a.amineAmountTTC ?? 0) - (b.amineAmountTTC ?? 0)) * dir;
          case 'status': return ((a.status || '').localeCompare(b.status || '')) * dir;
          default: return 0;
        }
      });
    }

    return filtered;
  }, [invoices, searchTerm, selectedMonth, selectedYear, selectedOpportunityType, selectedStatus, sortField, sortDirection]);

  const currentInvoices = filteredInvoices;

  // Calculer les totaux des factures affichées (Foodinnov)
  const totals = currentInvoices.reduce((acc, invoice) => {
    acc.totalHT += Number(invoice.montantHT) || 0;
    acc.totalTTC += Number(invoice.montantTTC) || 0;
    acc.totalAmineAmount += Number(invoice.amineAmount) || 0;
    acc.totalAmineAmountTTC += Number(invoice.amineAmountTTC) || 0;
    return acc;
  }, { totalHT: 0, totalTTC: 0, totalAmineAmount: 0, totalAmineAmountTTC: 0 });

  // Calculer les totaux par statut
  const totalsByStatus = currentInvoices.reduce((acc, invoice) => {
    const status = invoice.status || 'En attente de Paiement';
    const isReglee = status === 'Réglée';
    
    if (isReglee) {
      acc.reglee.totalHT += Number(invoice.montantHT) || 0;
      acc.reglee.totalTTC += Number(invoice.montantTTC) || 0;
      acc.reglee.totalAmineAmount += Number(invoice.amineAmount) || 0;
      acc.reglee.totalAmineAmountTTC += Number(invoice.amineAmountTTC) || 0;
    } else {
      acc.enAttente.totalHT += Number(invoice.montantHT) || 0;
      acc.enAttente.totalTTC += Number(invoice.montantTTC) || 0;
      acc.enAttente.totalAmineAmount += Number(invoice.amineAmount) || 0;
      acc.enAttente.totalAmineAmountTTC += Number(invoice.amineAmountTTC) || 0;
    }
    return acc;
  }, { 
    reglee: { totalHT: 0, totalTTC: 0, totalAmineAmount: 0, totalAmineAmountTTC: 0 },
    enAttente: { totalHT: 0, totalTTC: 0, totalAmineAmount: 0, totalAmineAmountTTC: 0 }
  });

  // Filtrage et pagination pour les factures RegExport
  const filteredRegexportInvoices = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    let filtered = regexportInvoices.filter(
      (invoice) => {
        const matchesSearch = invoice.companyId.toLowerCase().includes(lower) ||
          invoice.opportunityType.toLowerCase().includes(lower) ||
          invoice.id.toLowerCase().includes(lower);

        // Statut normalisé ("Pas Réglée" et vide = "En attente de Paiement")
        const effectiveStatus = invoice.status === 'Réglée' || invoice.status === 'Gelée'
          ? invoice.status
          : 'En attente de Paiement';
        const matchesStatus = selectedStatus === "all" || effectiveStatus === selectedStatus;

        // Vérifier que la date existe avant de l'utiliser
        if (!invoice.date) {
          // Si pas de date, ne pas filtrer par mois/année
          return matchesSearch && matchesStatus;
        }

        const invoiceDate = invoice.date.toDate();
        const matchesMonth = selectedMonth === "all" || invoiceDate.getMonth() === parseInt(selectedMonth);
        const matchesYear = selectedYear === "all" || invoiceDate.getFullYear() === parseInt(selectedYear);
        const matchesOpportunityType = selectedOpportunityType === "all" || invoice.opportunityType === selectedOpportunityType;

        // Filtrer les sociétés contenant "FOODINNOV" ou "NUTRI-LAURE INNOVATION" dans le nom
        // (tolérant à la variation tiret/espace pour "NUTRI-LAURE")
        const companyUpper = invoice.companyId.toUpperCase();
        const matchesFoodinnov = companyUpper.includes("FOODINNOV") ||
          (companyUpper.includes("NUTRI") && companyUpper.includes("LAURE"));

        return matchesSearch && matchesMonth && matchesYear && matchesOpportunityType && matchesFoodinnov && matchesStatus;
      }
    );

    // Appliquer le tri si nécessaire
    if (sortField2 !== 'none') {
      filtered.sort((a, b) => {
        const dir = sortDirection2 === 'asc' ? 1 : -1;
        switch (sortField2) {
          case 'date': {
            const da = a.date?.toMillis?.() ?? 0;
            const db = b.date?.toMillis?.() ?? 0;
            return (da - db) * dir;
          }
          case 'id': return ((a.id || '').localeCompare(b.id || '')) * dir;
          case 'companyId': return ((a.companyId || '').localeCompare(b.companyId || '')) * dir;
          case 'opportunityType': return ((a.opportunityType || '').localeCompare(b.opportunityType || '')) * dir;
          case 'qte': return ((a.qte ?? 0) - (b.qte ?? 0)) * dir;
          case 'pu': return ((a.pu ?? 0) - (b.pu ?? 0)) * dir;
          case 'montantHT': return ((a.montantHT ?? 0) - (b.montantHT ?? 0)) * dir;
          case 'montantTTC': return ((a.montantTTC ?? 0) - (b.montantTTC ?? 0)) * dir;
          case 'status': return ((a.status || '').localeCompare(b.status || '')) * dir;
          default: return 0;
        }
      });
    }

    console.log(`[RegExport] Tableau affiché après tri/filtre (${filtered.length} factures) :`, filtered.map(inv => ({
      id: inv.id,
      companyId: inv.companyId,
      opportunityType: inv.opportunityType,
      qte: inv.qte,
      pu: inv.pu,
      montantHT: inv.montantHT,
      montantTTC: inv.montantTTC,
      date: inv.date?.toDate?.()?.toLocaleDateString('fr-FR'),
      status: inv.status,
      uid: inv.uid,
    })));
    return filtered;
  }, [regexportInvoices, searchTerm, selectedMonth, selectedYear, selectedOpportunityType, selectedStatus, sortField2, sortDirection2]);

  const currentRegexportInvoices = filteredRegexportInvoices;

  // Montant réellement facturé (repli sur le montant opportunité si le champ facturé est absent)
  const factureHT = (inv: any) => Number(inv.montantFactureHT ?? inv.montantHT) || 0;
  const factureTTC = (inv: any) => Number(inv.montantFactureTTC ?? inv.montantTTC) || 0;

  // Calculer les totaux des factures RegExport affichées (montants facturés)
  const regexportTotals = currentRegexportInvoices.reduce((acc, invoice) => {
    acc.totalHT += factureHT(invoice);
    acc.totalTTC += factureTTC(invoice);
    return acc;
  }, { totalHT: 0, totalTTC: 0 });

  // Calculer les totaux par statut pour RegExport
  const regexportTotalsByStatus = currentRegexportInvoices.reduce((acc, invoice) => {
    const status = invoice.status || 'En attente de Paiement';

    if (status === 'Gelée') {
      // Les factures gelées ne sont comptées ni dans les réglées ni dans les en attente
      acc.gelee.totalHT += factureHT(invoice);
      acc.gelee.totalTTC += factureTTC(invoice);
    } else if (status === 'Réglée') {
      acc.reglee.totalHT += factureHT(invoice);
      acc.reglee.totalTTC += factureTTC(invoice);
    } else {
      acc.enAttente.totalHT += factureHT(invoice);
      acc.enAttente.totalTTC += factureTTC(invoice);
    }
    return acc;
  }, {
    reglee: { totalHT: 0, totalTTC: 0 },
    enAttente: { totalHT: 0, totalTTC: 0 },
    gelee: { totalHT: 0, totalTTC: 0 }
  });

  // Détecter le devis pré-sélectionné depuis la navigation
  useEffect(() => {
    const state = location.state as { preselectedDevis?: FirestoreDevis } | null;
    if (state?.preselectedDevis) {
      setPreselectedDevis(state.preselectedDevis);
      setShowModal(true);
      // Nettoyer le state pour éviter de rouvrir le modal à chaque re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Chargement des factures et des types de demandes
  useEffect(() => {
    const docRef = doc(db, "prospects", "foodinnovCompanies");
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const allInvoices: Invoice[] = [];
        
        // Parcourir toutes les sociétés pour récupérer leurs factures
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          // Exclure les factures de ZakariaELORCHE (elles sont gérées dans FactureZak.tsx)
          if (companyId !== 'ZakariaELORCHE' && companyData.invoices) {
            companyData.invoices.forEach((invoice: Invoice) => {
              allInvoices.push({
                ...invoice,
                companyId,
                source: 'foodinnov'
              });
            });
          }
        });

        // Log pour debug
        console.log('Factures récupérées depuis Firestore :', allInvoices.length, allInvoices);

        // Extraire les opportunités foodinnov par société
        const foodinnovOppsMap: {[key: string]: any[]} = {};
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          if (companyData.opportunities) {
            foodinnovOppsMap[companyId] = companyData.opportunities;
          }
        });
        setFoodinnovOpportunities(foodinnovOppsMap);

        // Trier les factures par date (plus récentes en premier)
        allInvoices.sort((a, b) => {
          // Vérifier que les dates existent avant de les comparer
          if (!a.date || !b.date) {
            // Si une des dates est manquante, mettre la facture sans date à la fin
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
          }
          
          // Convertir les dates en millisecondes pour la comparaison
          const dateA = a.date?.toMillis?.() || 0;
          const dateB = b.date?.toMillis?.() || 0;
          
          return dateB - dateA; // Tri décroissant (plus récent en premier)
        });
        setInvoices(allInvoices);

        // Charger les types de demandes depuis Firestore
        let types = (data.opportunityTypes as string[]) || [];
        
        // Si pas de types dans opportunityTypes, extraire depuis les factures existantes
        if (types.length === 0) {
          const existingTypes = new Set<string>();
          allInvoices.forEach(invoice => {
            if (invoice.opportunityType) {
              existingTypes.add(invoice.opportunityType);
            }
          });
          types = Array.from(existingTypes).sort();
          
          // Sauvegarder automatiquement les types extraits
          if (types.length > 0) {
            updateDoc(docRef, {
              opportunityTypes: types
            }).then(() => {
              console.log("Types extraits et sauvegardés pour FacturesFoodinnov:", types);
            }).catch(error => {
              console.error("Erreur lors de la sauvegarde des types FacturesFoodinnov:", error);
            });
          }
        }
        
        setOpportunityTypes(types);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    cleanupInvoices();
  }, []);

  // Chargement des factures RegExport (collection companies)
  useEffect(() => {
    const docRef = doc(db, "prospects", "companies");
    const unsubscribe = onSnapshot(docRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const allInvoices: Invoice[] = [];
        
        // Parcourir toutes les sociétés pour récupérer leurs factures
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          // Exclure les factures de ZakariaELORCHE (elles sont gérées dans FactureZak.tsx)
          if (companyId !== 'ZakariaELORCHE' && companyData.invoices) {
            companyData.invoices.forEach((invoice: Invoice) => {
              allInvoices.push({
                ...invoice,
                companyId,
                source: 'regexport'
              });
            });
          }
        });

        // Extraire les opportunités regexport par société
        const regexportOppsMap: {[key: string]: any[]} = {};
        Object.entries(data).forEach(([companyId, companyData]: [string, any]) => {
          if (companyData.opportunities) {
            regexportOppsMap[companyId] = companyData.opportunities;
          }
        });
        setRegexportOpportunities(regexportOppsMap);

        // Trier les factures par date (plus récentes en premier)
        allInvoices.sort((a, b) => {
          // Vérifier que les dates existent avant de les comparer
          if (!a.date || !b.date) {
            // Si une des dates est manquante, mettre la facture sans date à la fin
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
          }

          // Convertir les dates en millisecondes pour la comparaison
          const dateA = a.date?.toMillis?.() || 0;
          const dateB = b.date?.toMillis?.() || 0;

          return dateB - dateA; // Tri décroissant (plus récent en premier)
        });
        console.log('[RegExport] Factures chargées :', allInvoices.map(inv => ({
          id: inv.id,
          companyId: inv.companyId,
          opportunityType: inv.opportunityType,
          opportunityDate: inv.opportunityDate,
          date: inv.date?.toDate?.()?.toLocaleDateString('fr-FR'),
          montantHT: inv.montantHT,
        })));
        setRegexportInvoices(allInvoices);
      }
    });

    return () => unsubscribe();
  }, []);

  // Gestion des factures
  const handleAddInvoice = () => {
    setSelectedInvoice(null);
    setPreselectedDevis(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedInvoice(null);
    setPreselectedDevis(null);
  };

  const handleEditInvoice = (invoice: Invoice) => {
    // S'assurer que toutes les données nécessaires sont présentes
    const invoiceToEdit = {
      ...invoice,
      companyId: invoice.companyId,
      opportunityType: invoice.opportunityType,
      qte: invoice.qte,
      pu: invoice.pu,
      montantHT: invoice.montantHT,
      montantTTC: invoice.montantTTC,
      amineAmount: invoice.amineAmount,
      amineAmountTTC: invoice.amineAmountTTC,
      pourcentage: invoice.pourcentage,
      date: invoice.date,
      id: invoice.id
    };
    setSelectedInvoice(invoiceToEdit);
    setShowModal(true);
  };

  const handleDeleteInvoice = (invoice: Invoice) => {
    setInvoiceToDelete(invoice); // Set the invoice to be deleted, opening the confirmation modal
  };

  // Gestionnaires pour les factures RegExport
  const handleEditRegexportInvoice = (invoice: Invoice) => {
    console.log("=== ÉDITION FACTURE REGEXPORT ===");
    console.log("Facture RegExport à éditer:", invoice);
    setSelectedRegexportInvoice(invoice);
    setShowRegexportModal(true);
  };

  const handleDeleteRegexportInvoice = (invoice: Invoice) => {
    setInvoiceToDelete(invoice);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;

    try {
      const docName = invoiceToDelete.source === 'regexport' ? "companies" : "foodinnovCompanies";
      const docRef = doc(db, "prospects", docName);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        toast.error("Document non trouvé");
        return;
      }

      const data = docSnap.data();
      const companyData = data[invoiceToDelete.companyId];

      if (!companyData || !companyData.invoices) {
        toast.error("Données de la société non trouvées");
        return;
      }

      // Suppression basée sur un identifiant unique (uid), sinon fallback sur tous les champs principaux
      const updatedInvoices = companyData.invoices.filter((inv: any) => {
        if (invoiceToDelete.uid && inv.uid) {
          return inv.uid !== invoiceToDelete.uid;
        }
        // fallback si pas de uid : on compare tous les champs principaux
        return !(
          inv.id === invoiceToDelete.id &&
          inv.opportunityType === invoiceToDelete.opportunityType &&
          inv.qte === invoiceToDelete.qte &&
          inv.pu === invoiceToDelete.pu &&
          inv.montantHT === invoiceToDelete.montantHT &&
          inv.montantTTC === invoiceToDelete.montantTTC &&
          inv.amineAmount === invoiceToDelete.amineAmount &&
          inv.amineAmountTTC === invoiceToDelete.amineAmountTTC &&
          inv.date?.toMillis?.() === invoiceToDelete.date?.toMillis?.()
        );
      });

      // Mettre à jour les factures de la société
      await updateDoc(docRef, {
        [`${invoiceToDelete.companyId}.invoices`]: updatedInvoices
      });

      // Log de la suppression
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Suppression facture Foodinnov",
          `Facture Foodinnov supprimée: ${invoiceToDelete.id} pour ${invoiceToDelete.companyId} - Montant: ${invoiceToDelete.montantHT}€`
        );
      }

      toast.success("Facture supprimée avec succès");
      setInvoiceToDelete(null);

    } catch (error) {
      console.error("Erreur lors de la suppression de la facture:", error);
      toast.error("Erreur lors de la suppression de la facture");
    }
  };

  const cancelDeleteInvoice = () => {
    setInvoiceToDelete(null); // Close the confirmation modal without deleting
  };

  const handleExportCSV = () => {
    const headers = [
      "ID",
      "Société",
      "Type de demande",
      "Quantité",
      "Prix unitaire",
      "Remise (%)",
      "Montant HT",
      "Montant TTC",
      "Pourcentage",
      "Montant à régler à Amine",
      "Montant TTC à régler à Amine",
      "Date de création"
    ];

    const csvData = invoices.map(invoice => [
      invoice.id,
      invoice.companyId,
      invoice.opportunityType,
      invoice.qte,
      invoice.pu,
      (invoice.qte && invoice.pu && invoice.montantHT !== undefined && invoice.montantHT !== null) ?
        (100 * (1 - (invoice.montantHT / (invoice.qte * invoice.pu)))).toFixed(2) + ' %' : '-',
      invoice.montantHT,
      invoice.montantTTC,
      invoice.pourcentage,
      invoice.amineAmount,
      invoice.amineAmountTTC,
      invoice.date ? invoice.date.toDate().toLocaleDateString() : "-"
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `factures_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatValue = (value: any, type: string) => {
    if (value === undefined || value === null) return "-";
    if (type === "date") {
      // Vérifier que la valeur a une méthode toDate
      if (value && typeof value.toDate === 'function') {
        return value.toDate().toLocaleDateString();
      }
      // Si c'est déjà une Date
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }
      return "-";
    }
    if (type === "currency") {
      return `${Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
    }
    return value.toString();
  };

  const months = [
    { value: "all", label: "Afficher tout" },
    { value: "0", label: "Janvier" },
    { value: "1", label: "Février" },
    { value: "2", label: "Mars" },
    { value: "3", label: "Avril" },
    { value: "4", label: "Mai" },
    { value: "5", label: "Juin" },
    { value: "6", label: "Juillet" },
    { value: "7", label: "Août" },
    { value: "8", label: "Septembre" },
    { value: "9", label: "Octobre" },
    { value: "10", label: "Novembre" },
    { value: "11", label: "Décembre" }
  ];

  const currentYear = new Date().getFullYear();
  const years = [
    { value: "all", label: "Afficher Tout" },    ...Array.from({ length: 5 }, (_, i) => ({
      value: (currentYear - i).toString(),
      label: (currentYear - i).toString()
    }))
  ];

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setInvoiceToPreview(null);
    if (invoicePdfPreviewUrl) {
       URL.revokeObjectURL(invoicePdfPreviewUrl);
       setInvoicePdfPreviewUrl(null);
    }
  };

  // Handler pour ouvrir la modale de modification de statut
  const handleEditStatus = (invoice: Invoice) => {
    setInvoiceToEditStatus(invoice);
    setShowStatusModal(true);
  };

  // Handler pour fermer la modale de modification de statut
  const handleCloseStatusModal = () => {
    setShowStatusModal(false);
    setInvoiceToEditStatus(null);
  };

  // Handler pour rafraîchir les données après mise à jour du statut
  const handleStatusUpdated = () => {
    // Les données se rafraîchissent automatiquement grâce à onSnapshot
    console.log("Statut mis à jour, données rafraîchies");
  };

  // Handler pour démarrer l'édition du numéro de facture
  const handleStartEditInvoiceNumber = (invoice: Invoice, idx: number) => {
    // Utiliser la même clé unique que pour le key du tr
    const uniqueKey = invoice.uid || (invoice.id + '-' + invoice.companyId + '-' + (invoice.date?.toMillis?.() || idx));
    setEditingInvoiceNumber(uniqueKey);
    setEditingInvoiceNumberValue(invoice.invoiceNumber || "");
  };

  // Handler pour annuler l'édition du numéro de facture
  const handleCancelEditInvoiceNumber = () => {
    setEditingInvoiceNumber(null);
    setEditingInvoiceNumberValue("");
  };

  // Handler pour sauvegarder le numéro de facture
  const handleSaveInvoiceNumber = async (invoice: Invoice) => {
    try {
      const docRef = doc(db, "prospects", "foodinnovCompanies");
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        toast.error("Document non trouvé");
        return;
      }

      const data = docSnap.data();
      const companyData = data[invoice.companyId];

      if (!companyData || !companyData.invoices) {
        toast.error("Données de la société non trouvées");
        return;
      }

      // Trouver et mettre à jour la facture
      const updatedInvoices = companyData.invoices.map((inv: any) => {
        const matches = (invoice.uid && inv.uid) 
          ? inv.uid === invoice.uid 
          : (inv.id === invoice.id && inv.opportunityType === invoice.opportunityType);
        
        if (matches) {
          return {
            ...inv,
            invoiceNumber: editingInvoiceNumberValue.trim() || undefined
          };
        }
        return inv;
      });

      await updateDoc(docRef, {
        [`${invoice.companyId}.invoices`]: updatedInvoices
      });

      // Log de la modification
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Modification numéro de facture Foodinnov",
          `Numéro de facture modifié: ${editingInvoiceNumberValue} pour ${invoice.companyId} - Facture: ${invoice.id}`
        );
      }

      toast.success("Numéro de facture mis à jour avec succès");
      setEditingInvoiceNumber(null);
      setEditingInvoiceNumberValue("");
    } catch (error) {
      console.error("Erreur lors de la mise à jour du numéro de facture:", error);
      toast.error("Erreur lors de la mise à jour du numéro de facture");
    }
  };

  // Handlers pour le tri des colonnes
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'date' ? 'desc' : 'asc');
    }
  };

  const handleSort2 = (field: string) => {
    if (sortField2 === field) {
      setSortDirection2(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField2(field);
      setSortDirection2(field === 'date' ? 'desc' : 'asc');
    }
  };

  const renderSortIcon = (field: string) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up" style={{ color: '#6c757d', opacity: 0.4, fontSize: '0.75rem', marginLeft: '4px' }} />;
    return sortDirection === 'asc'
      ? <i className="bi bi-arrow-up" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />
      : <i className="bi bi-arrow-down" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />;
  };

  const renderSortIcon2 = (field: string) => {
    if (sortField2 !== field) return <i className="bi bi-arrow-down-up" style={{ color: '#6c757d', opacity: 0.4, fontSize: '0.75rem', marginLeft: '4px' }} />;
    return sortDirection2 === 'asc'
      ? <i className="bi bi-arrow-up" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />
      : <i className="bi bi-arrow-down" style={{ color: '#007bff', fontSize: '0.75rem', marginLeft: '4px' }} />;
  };

  // Handler pour dupliquer une facture
  // Utilitaires PDF
  const rgbFromHex = (hex: string) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return rgb(r, g, b);
  };

  const formatFieldValue = (value: any, fieldName?: string): string => {
    if (value instanceof Timestamp) {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (value && typeof value.toDate === 'function') {
      return value.toDate().toLocaleDateString("fr-FR");
    }
    if (fieldName === 'amount' && typeof value === 'number') {
      return value.toFixed(2);
    }
    return value?.toString() || "";
  };

  const handleGenerateInvoicePdf = async (invoice: Invoice) => {
    // Si la facture possède un PDF joint, on affiche ce PDF
    if ((invoice as any).fileUrl) {
      setInvoicePdfPreviewUrl((invoice as any).fileUrl);
      setInvoiceToPreview(invoice);
      setShowPreviewModal(true);
      toast.success("Aperçu du PDF joint ouvert.");
      return;
    }

    // Sinon, comportement actuel : génération du PDF
    try {
      // 1. Récupérer les coordonnées depuis Coordinates/maFacture
      const coords = await getPDFCoordinatesFromStorage("maFacture");
      console.log("Coordonnées Facture chargées:", coords);

      if (!coords) {
        toast.error("Coordonnées PDF Facture non trouvées.");
        return;
      }

      // 2. Récupérer les infos complètes de l'entreprise
      const companyDocRef = doc(db, "prospects", "companies");
      const companyDocSnap = await getDoc(companyDocRef);
      let companyData: any | undefined;

      if (companyDocSnap.exists()) {
        const allCompaniesData = companyDocSnap.data();
        companyData = allCompaniesData[invoice.companyId];
        console.log("Données entreprise chargées:", companyData);
      } else {
        toast.error("Données entreprises non trouvées dans Firestore.");
        return;
      }
      
      if (!companyData) {
         toast.error(`Données spécifiques pour l'entreprise ${invoice.companyId} non trouvées.`);
         return;
      }

      // 3. Chargement du template PDF de la facture
      const pdfPath = "/pdfs/facture.pdf";
      const existingPdfBytes = await fetch(pdfPath).then((res) => {
        if (!res.ok) throw new Error(`Échec du chargement du modèle PDF Facture: ${res.status}`);
        return res.arrayBuffer();
      });
      
      const pdfDoc = await PDFDocument.load(existingPdfBytes);
      const pages = pdfDoc.getPages();
      if (pages.length === 0) throw new Error("Le modèle PDF Facture n'a aucune page");

      const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const fontItalic = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      const fontBoldItalic = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);

      // 4. Remplissage des champs sur le PDF
      for (const [pageNumber, pageCoords] of Object.entries(coords)) {
        const pageIndex = parseInt(pageNumber) - 1;
        if (pageIndex < 0 || pageIndex >= pages.length) continue;

        const page = pages[pageIndex];
        const { height } = page.getSize();
        
         // Triez les champs par coordonnée Y pour un dessin cohérent (de haut en bas) si nécessaire
         const sortedFields = Object.entries(pageCoords).sort(([, a], [, b]) => {
           return a.y - b.y;
         });

        for (const [fullFieldName, fieldConfig] of sortedFields) {
          const { x, y, size = 12, color = "#000000", bold = false, italic = false } = fieldConfig as any;
          
          // Extraction du nom de champ et de la source
          const parts = fullFieldName.split('-');
          const source = parts[0];
          const fieldName = parts.slice(1).join('-');

          let value = '';
          // Logique pour extraire les valeurs correctement de l'objet 'invoice' et 'companyData' en fonction de 'source' et 'fieldName'
          switch (source) {
            case 'Informations Facture':
               // Mapper les noms de champs de coordonnées aux propriétés de l'objet invoice
               switch (fieldName) {
                 case 'factureNumber': // Assumons que cela correspond à l'ID de la facture
                 case 'id':
                   value = formatFieldValue(invoice.id, fieldName);
                   break;
                 case 'factureDate': // Assumons que cela correspond à la date de la facture
                 case 'date':
                   value = formatFieldValue(invoice.date, fieldName);
                   break;
                 case 'totalHT': // Mappage pour le désalignement identifié
                 case 'montantHT':
                   value = formatFieldValue(invoice.montantHT, 'currency');
                   break;
                 case 'totalTTC': // Mappage pour le désalignement identifié
                 case 'montantTTC':
                   value = formatFieldValue(invoice.montantTTC, 'currency');
                   break;
                 case 'qte':
                   value = formatFieldValue(invoice.qte, fieldName);
                   break;
                 case 'pu': // Prix unitaire
                   value = formatFieldValue(invoice.pu, 'currency');
                   break;
                 case 'opportunityType':
                   value = formatFieldValue(invoice.opportunityType, fieldName);
                   break;
                 case 'updatedAt':
                   value = formatFieldValue(invoice.updatedAt, fieldName);
                   break;
                 // Ajoutez d'autres champs de facture ici si nécessaire
                 default:
                   console.warn(`Champ de facture inconnu ou non mappé: Informations Facture-${fieldName}`);
                   // Tentez un accès direct au cas où
                    if (fieldName in invoice) {
                      value = formatFieldValue((invoice as any)[fieldName], fieldName);
                   } else {
                     console.warn(`Champ de facture inconnu dans l'objet invoice: ${fieldName}`);
                   }
                   break;
               }
               break;
            case 'Informations Entreprise':
               // Accéder aux propriétés de l'objet 'companyData' (assumant que fieldName correspond à la propriété)
               if (companyData && fieldName in companyData) {
                  value = formatFieldValue((companyData as any)[fieldName], fieldName);
               } else {
                  console.warn(`Champ de société inconnu dans les données de l'entreprise: Informations Entreprise-${fieldName}`);
               }
               break;
             case 'Contact Principal':
               // Accéder aux propriétés du contact principal (assumant que fieldName correspond à la propriété)
                if (companyData && fieldName in companyData) {
                     value = formatFieldValue((companyData as any)[fieldName], fieldName); // Les champs contact sont aussi des propriétés de companyData
                } // TODO: Gérer d'autres contacts si les coordonnées les incluent
                else {
                    console.warn(`Champ de contact principal inconnu dans les données de l'entreprise: Contact Principal-${fieldName}`);
                }
               break;
            default:
              // Source inconnue ou non gérée (ne devrait plus arriver avec la correction précédente du type FieldSource)
              console.warn(`Source de champ inconnue: ${source}`);
              break;
          }

          if (value) {
            // Nouveau calcul X/Y avec FactureConfig
            const xPdf = (x * FACTURE_PDF_SCALE) + FACTURE_X_OFFSET_PDF_GENERATION;
            const yPdf = (y * FACTURE_PDF_SCALE) + FACTURE_Y_OFFSET_PDF_GENERATION;
            const adjustedYForPdfLib = height - yPdf;
            const finalY = adjustedYForPdfLib - (size * (FACTURE_FONT_SIZE_OFFSET || 0)) + (FACTURE_Y_OFFSET_CORRECTION || 0);

            let font = fontRegular;
            if (bold && italic) font = fontBoldItalic;
            else if (bold) font = fontBold;
            else if (italic) font = fontItalic;
            
            const textColor = color ? rgbFromHex(color) : rgb(0, 0, 0);

            page.drawText(value, {
              x: xPdf,
              y: finalY,
              size: size,
              font,
              color: textColor,
            });
          }
        }
      }

      // 5. Génération du PDF final
      const pdfBytes = await pdfDoc.save();
      // Conversion explicite pour garantir la compatibilité avec BlobPart
      const uint8Array = new Uint8Array(pdfBytes.length);
      uint8Array.set(pdfBytes);
      const blob = new Blob([uint8Array], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      // Stocker l'URL et ouvrir la modale
      setInvoicePdfPreviewUrl(blobUrl);
      setInvoiceToPreview(invoice);
      setShowPreviewModal(true);
      toast.success("PDF Facture généré avec succès !");

    } catch (error) {
      console.error("Erreur génération PDF Facture:", error);
      toast.error(`Échec génération PDF Facture: ${error instanceof Error ? error.message : String(error)}`);
      setInvoicePdfPreviewUrl(null);
      setInvoiceToPreview(null);
      setShowPreviewModal(false);
    }
  };

  const handleDuplicateInvoice = async (invoice: Invoice) => {
    try {
      const docRef = doc(db, "prospects", "foodinnovCompanies");
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        toast.error("Document non trouvé");
        return;
      }
      const data = docSnap.data();
      const companyData = data[invoice.companyId];
      if (!companyData || !companyData.invoices) {
        toast.error("Données de la société non trouvées");
        return;
      }
      // Trouver l'index de la facture à dupliquer
      const index = companyData.invoices.findIndex((inv: any) => (invoice.uid && inv.uid) ? inv.uid === invoice.uid : (inv.id === invoice.id && inv.opportunityType === invoice.opportunityType));
      if (index === -1) {
        toast.error("Facture à dupliquer non trouvée");
        return;
      }
      // Créer une nouvelle facture avec un nouvel UID unique
      const newInvoice = {
        ...invoice,
        id: 'Copie',
        uid: Date.now().toString() + Math.random().toString(36).substring(2, 8),
        date: Timestamp.now(),
        updatedAt: Timestamp.now(),
        isSigned: false
      };
      // Insérer la copie juste après l'original
      const updatedInvoices = [...companyData.invoices];
      updatedInvoices.splice(index + 1, 0, newInvoice);
      await updateDoc(docRef, {
        [`${invoice.companyId}.invoices`]: updatedInvoices
      });
      
      // Log de la duplication
      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        await logUserAction(
          currentUser.email,
          "Duplication facture Foodinnov",
          `Facture Foodinnov dupliquée: ${invoice.id} pour ${invoice.companyId} - Montant: ${invoice.montantHT}€`
        );
      }
      
      toast.success("Facture dupliquée avec succès");
    } catch (error) {
      console.error("Erreur lors de la duplication de la facture:", error);
      toast.error("Erreur lors de la duplication de la facture");
    }
  };

  // Ajout du state pour le rôle utilisateur
  const [userRole, setUserRole] = useState<string | null>(null);

  // Récupération du rôle utilisateur depuis Firestore
  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user && user.email) {
        const userDoc = await getDoc(doc(db, "Users", user.email));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    };
    fetchUserRole();
  }, []);

  return (
    <div className="factures-container">
      <ToastContainer position="top-right" autoClose={2000} />
      <Header 
        isAuthenticated={true} 
        onLoginClick={() => navigate('/login')} 
        onHomeClick={() => navigate('/')} 
        onLogoutClick={handleLogout}
      />
      {/* Section 1 - Factures Foodinnov */}
      <section style={{ marginBottom: '40px' }}>
        <h2 className="factures-title" style={{ color: '#424272' }}>Liste des Factures Foodinnov :</h2>

        <div className="factures-actions-bar">
        {userRole && userRole !== 'foodinnov' && (
          <button 
            className="add-opportunite-button" 
            onClick={handleAddInvoice} 
            style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#424272' }}
          >
            + Ajouter une facture
          </button>
        )}
        <button 
          className="add-opportunite-button" 
          onClick={handleExportCSV} 
          style={{ fontFamily: 'Inter, sans-serif', backgroundColor: '#424272' }}
        >
          Exporter en CSV
        </button>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
          <button type="button" title="Mois précédent" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 0 ? 11 : m - 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-left"></i>
          </button>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="opportunites-search-bar"
            style={{ fontFamily: 'Inter, sans-serif', maxWidth: "130px" }}
          >
            {months.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
          <button type="button" title="Mois suivant" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 11 ? 0 : m + 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "120px" }}
        >
          {years.map((year) => (
            <option key={year.value} value={year.value}>
              {year.label}
            </option>
          ))}
        </select>
        <select
          value={selectedOpportunityType}
          onChange={(e) => setSelectedOpportunityType(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "200px" }}
        >
          <option value="all">Tous les types de demande</option>
          {opportunityTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="opportunites-search-bar"
          style={{ fontFamily: 'Inter, sans-serif', maxWidth: "180px" }}
        >
          <option value="all">Tous les statuts</option>
          <option value="En attente de Paiement">En attente de Paiement</option>
          <option value="Réglée">Réglée</option>
          <option value="Gelée">Gelée</option>
        </select>
        <input
          type="text"
          className="opportunites-search-bar"
          placeholder="Rechercher une facture..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ fontFamily: 'Inter, sans-serif' }}
        />
      </div>

      <div className="factures-table-container">
        <table className="factures-table" style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.9rem' }}>
          <thead>
            <tr>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('invoiceNumber')}
              >
                Numéro de facture{renderSortIcon('invoiceNumber')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('companyId')}
              >
                Société{renderSortIcon('companyId')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('opportunityType')}
              >
                Type de demande{renderSortIcon('opportunityType')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('qte')}
              >
                Qté{renderSortIcon('qte')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('pu')}
              >
                Prix unitaire{renderSortIcon('pu')}
              </th>
              <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Remise (%)</th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantHT')}
              >
                Montant (HT){renderSortIcon('montantHT')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('montantTTC')}
              >
                Montant (TTC){renderSortIcon('montantTTC')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', borderLeft: '2px solid #dee2e6' }}
                onClick={() => handleSort('pourcentage')}
              >
                %{renderSortIcon('pourcentage')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('amineAmount')}
              >
                Montant HT à <br/>régler à RegExport{renderSortIcon('amineAmount')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('amineAmountTTC')}
              >
                Montant TTC à <br/>régler à RegExport{renderSortIcon('amineAmountTTC')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('date')}
              >
                Date de création{renderSortIcon('date')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort('status')}
              >
                Statut{renderSortIcon('status')}
              </th>
              {userRole === "foodinnov" && (
                <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Modifier Statut</th>
              )}
              {userRole === "admin" && (
                <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentInvoices.length === 0 ? (
              <tr>
                <td colSpan={userRole === "admin" ? 14 : (userRole === "foodinnov" ? 13 : 12)} style={{ textAlign: "center", fontFamily: 'Inter, sans-serif' }}>Aucune facture trouvée.</td>
              </tr>
            ) : (
              <>
                {currentInvoices.map((invoice, idx) => {
                  const invoiceKey = invoice.uid || (invoice.id + '-' + idx);
                  // Utiliser la même clé unique pour identifier la facture en édition
                  const uniqueKey = invoice.uid || (invoice.id + '-' + invoice.companyId + '-' + (invoice.date?.toMillis?.() || idx));
                  const isEditing = editingInvoiceNumber === uniqueKey;
                  
                  return (
                  <tr key={invoiceKey}>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      {isEditing && userRole === "foodinnov" ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <input
                            type="text"
                            value={editingInvoiceNumberValue}
                            onChange={(e) => setEditingInvoiceNumberValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveInvoiceNumber(invoice);
                              } else if (e.key === 'Escape') {
                                handleCancelEditInvoiceNumber();
                              }
                            }}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #007bff',
                              borderRadius: '4px',
                              fontSize: '0.9rem',
                              width: '120px'
                            }}
                            autoFocus
                          />
                          <i
                            className="bi bi-check-circle"
                            onClick={() => handleSaveInvoiceNumber(invoice)}
                            style={{
                              color: '#28a745',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              marginLeft: '4px'
                            }}
                            title="Enregistrer"
                          />
                          <i
                            className="bi bi-x-circle"
                            onClick={handleCancelEditInvoiceNumber}
                            style={{
                              color: '#dc3545',
                              cursor: 'pointer',
                              fontSize: '1.2rem',
                              marginLeft: '4px'
                            }}
                            title="Annuler"
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            cursor: userRole === "foodinnov" ? 'pointer' : 'default'
                          }}
                          onClick={() => userRole === "foodinnov" && handleStartEditInvoiceNumber(invoice, idx)}
                          title={userRole === "foodinnov" ? "Cliquer pour modifier" : ""}
                        >
                          <span>{invoice.invoiceNumber || "-"}</span>
                          {userRole === "foodinnov" && (
                            <i
                              className="bi bi-pencil-square"
                              style={{
                                color: '#007bff',
                                fontSize: '0.9rem',
                                marginLeft: '4px'
                              }}
                            />
                          )}
                        </div>
                      )}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.companyId}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      {(() => {
                        const opps = foodinnovOpportunities[invoice.companyId] || [];
                        const byDate = invoice.opportunityDate ? opps.find((opp: any) => opp.type === invoice.opportunityType && String(opp.date) === String(invoice.opportunityDate)) : null;
                        const byQtePu = opps.find((opp: any) => opp.type === invoice.opportunityType && Number(opp.qte) === Number(invoice.qte) && Math.abs(Number(opp.pu) - Number(invoice.pu)) < 0.01);
                        const byMontant = opps.find((opp: any) => opp.type === invoice.opportunityType && Math.abs(Number(opp.amount) - Number(invoice.montantHT)) < 0.01);
                        const byType = opps.find((opp: any) => opp.type === invoice.opportunityType);
                        const matchingOpp = byDate || byQtePu || byMontant || byType || null;
                        const matchStep = byDate ? 'date' : byQtePu ? 'qte+pu' : byMontant ? 'montantHT' : byType ? 'type seul' : 'aucun';
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {invoice.opportunityType}
                            <Tippy
                              content={
                                <div style={{ fontSize: '0.82rem', textAlign: 'left', padding: '2px 4px', lineHeight: '1.6' }}>
                                  {matchingOpp ? (
                                    <>
                                      <div><strong>Contact :</strong> {matchingOpp.contact || '-'}</div>
                                      <div><strong>Qté :</strong> {matchingOpp.qte ?? '-'}</div>
                                      <div><strong>PU :</strong> {matchingOpp.pu ? `${Number(matchingOpp.pu).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Remise :</strong> {matchingOpp.remise !== undefined ? `${matchingOpp.remise} %` : '-'}</div>
                                      <div><strong>Montant HT :</strong> {matchingOpp.amount ? `${Number(matchingOpp.amount).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Montant TTC :</strong> {matchingOpp.ttc ? `${Number(matchingOpp.ttc).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Date :</strong> {matchingOpp.date ? new Date(matchingOpp.date).toLocaleDateString('fr-FR') : '-'}</div>
                                      <div><strong>Statut :</strong> {matchingOpp.status || '-'}</div>
                                      {matchingOpp.description && <div><strong>Description :</strong> {matchingOpp.description}</div>}
                                    </>
                                  ) : (
                                    <div>Aucune opportunité associée</div>
                                  )}
                                </div>
                              }
                              placement="right"
                              allowHTML={false}
                              interactive={true}
                              onShow={() => {
                                console.group(`[Foodinnov] (i) — ${invoice.companyId} / ${invoice.opportunityType}`);
                                console.log('--- Facture ---', {
                                  id: invoice.id,
                                  companyId: invoice.companyId,
                                  opportunityType: invoice.opportunityType,
                                  opportunityDate: invoice.opportunityDate,
                                  qte: invoice.qte,
                                  pu: invoice.pu,
                                  montantHT: invoice.montantHT,
                                  montantTTC: invoice.montantTTC,
                                  date: invoice.date?.toDate?.()?.toLocaleDateString('fr-FR'),
                                  uid: invoice.uid,
                                });
                                console.log(`Match utilisé : ${matchStep}`);
                                console.log('--- Opportunité matchée ---', matchingOpp ?? 'aucune');
                                console.log(`--- Toutes les opps (${opps.length}) pour ${invoice.companyId} ---`, opps);
                                console.groupEnd();
                              }}
                            >
                              <i
                                className="bi bi-info-circle"
                                style={{ cursor: 'help', color: matchingOpp ? '#007bff' : '#adb5bd', fontSize: '0.9rem' }}
                              />
                            </Tippy>
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.qte}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.pu, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      {invoice.qte && invoice.pu ?
                        (invoice.montantHT !== undefined && invoice.montantHT !== null ?
                          (100 * (1 - (invoice.montantHT / (invoice.qte * invoice.pu)))).toFixed(2) + ' %'
                          : '-')
                        : '-'}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.montantHT, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.montantTTC, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif', borderLeft: '2px solid #dee2e6' }}>
                      {invoice.pourcentage ? Number(invoice.pourcentage).toFixed(2) + '%' : '-'}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.amineAmount, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.amineAmountTTC, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.date, "date")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        backgroundColor: invoice.status === 'Réglée' ? '#d4edda' : '#f8d7da',
                        color: invoice.status === 'Réglée' ? '#155724' : '#721c24',
                        border: `1px solid ${invoice.status === 'Réglée' ? '#c3e6cb' : '#f5c6cb'}`
                      }}>
                        {invoice.status === 'Pas Réglée' ? 'En attente de Paiement' : (invoice.status || 'En attente de Paiement')}
                      </span>
                    </td>
                    {userRole === "foodinnov" && (
                      <td style={{ fontFamily: 'Inter, sans-serif', textAlign: 'center' }}>
                        <i 
                          className="bi bi-pencil-square" 
                          title="Modifier le statut" 
                          onClick={() => handleEditStatus(invoice)} 
                          style={{ 
                            color: '#007bff', 
                            cursor: 'pointer', 
                            fontSize: '1.1rem',
                            padding: '4px'
                          }}
                        />
                      </td>
                    )}
                    {userRole === "admin" && (
                      <td className="action-icons" style={{ fontFamily: 'Inter, sans-serif' }}>
                        <i className="bi bi-pencil-square edit-icon" title="Modifier" onClick={() => handleEditInvoice(invoice)} style={{ color: '#007bff', cursor: 'pointer' }}></i>
                        <i className="bi bi-files copy-icon" title="Dupliquer" onClick={() => handleDuplicateInvoice(invoice)} style={{ color: '#28a745', cursor: 'pointer', marginLeft: 2 }}></i>
                        <i className="bi bi-trash delete-icon" title="Supprimer" onClick={() => handleDeleteInvoice(invoice)} style={{ color: '#dc3545', cursor: 'pointer', marginLeft: 2 }}></i>
                      </td>
                    )}
                  </tr>
                  );
                })}
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>Total</td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalHT, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalTTC, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif', borderLeft: '2px solid #dee2e6' }}>
                    {totals.totalHT > 0
                      ? (totals.totalAmineAmount / totals.totalHT * 100).toFixed(2) + '%'
                      : '-'}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalAmineAmount, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalAmineAmountTTC, "currency")}
                  </td>
                  <td colSpan={userRole === "admin" ? 4 : (userRole === "foodinnov" ? 3 : 2)}></td>
                </tr>
                <tr style={{ fontWeight: 'bold', backgroundColor: '#e8f4fd', color: '#0c5460' }}>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>Marge</td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalHT - totals.totalAmineAmount, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(totals.totalTTC - totals.totalAmineAmountTTC, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif', borderLeft: '2px solid #dee2e6' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td colSpan={userRole === "admin" ? 4 : (userRole === "foodinnov" ? 3 : 2)}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        
        {/* Totaux par statut */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '20px', justifyContent: 'space-around', fontFamily: 'Inter, sans-serif' }}>
          {/* Total Réglées */}
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#d4edda', 
            borderRadius: '5px', 
            border: '1px solid #c3e6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#155724', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-bank"></i>
              Total Réglées
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>HT:</strong> {formatValue(totalsByStatus.reglee.totalHT, "currency")}</div>
              <div><strong>TTC:</strong> {formatValue(totalsByStatus.reglee.totalTTC, "currency")}</div>
              <div><strong>RegExport HT:</strong> {formatValue(totalsByStatus.reglee.totalAmineAmount, "currency")}</div>
              <div><strong>RegExport TTC:</strong> {formatValue(totalsByStatus.reglee.totalAmineAmountTTC, "currency")}</div>
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #c3e6cb', paddingTop: '8px', marginTop: '4px' }}></div>
              <div><strong>Marge HT:</strong> {formatValue(totalsByStatus.reglee.totalHT - totalsByStatus.reglee.totalAmineAmount, "currency")}</div>
              <div><strong>Marge TTC:</strong> {formatValue(totalsByStatus.reglee.totalTTC - totalsByStatus.reglee.totalAmineAmountTTC, "currency")}</div>
            </div>
          </div>
          
          {/* Total En attente de Paiement */}
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8d7da', 
            borderRadius: '5px', 
            border: '1px solid #f5c6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#721c24', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-wallet2"></i>
              Total En attente de Paiement
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>HT:</strong> {formatValue(totalsByStatus.enAttente.totalHT, "currency")}</div>
              <div><strong>TTC:</strong> {formatValue(totalsByStatus.enAttente.totalTTC, "currency")}</div>
              <div><strong>RegExport HT:</strong> {formatValue(totalsByStatus.enAttente.totalAmineAmount, "currency")}</div>
              <div><strong>RegExport TTC:</strong> {formatValue(totalsByStatus.enAttente.totalAmineAmountTTC, "currency")}</div>
              <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f5c6cb', paddingTop: '8px', marginTop: '4px' }}></div>
              <div><strong>Marge HT:</strong> {formatValue(totalsByStatus.enAttente.totalHT - totalsByStatus.enAttente.totalAmineAmount, "currency")}</div>
              <div><strong>Marge TTC:</strong> {formatValue(totalsByStatus.enAttente.totalTTC - totalsByStatus.enAttente.totalAmineAmountTTC, "currency")}</div>
            </div>
          </div>
        </div>

      </div>
      </section>

      {/* Section 2 - Factures RegExport */}
      <section style={{ marginTop: '40px' }}>
        <h2 className="factures-title" style={{ color: '#424272' }}>Liste des Factures RegExport :</h2>
        
        {/* Filtres pour RegExport */}
        <div className="factures-actions-bar" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <button type="button" title="Mois précédent" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 0 ? 11 : m - 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
              <i className="bi bi-chevron-left"></i>
            </button>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="opportunites-search-bar"
              style={{ fontFamily: 'Inter, sans-serif', maxWidth: "130px" }}
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <button type="button" title="Mois suivant" onClick={() => { if (selectedMonth === 'all') return; const m = parseInt(selectedMonth); setSelectedMonth(String(m === 11 ? 0 : m + 1)); }} style={{ cursor: 'pointer', border: '1px solid #ced4da', background: '#fff', borderRadius: '4px', padding: '4px 8px', color: '#424272', fontWeight: 700, lineHeight: 1 }}>
              <i className="bi bi-chevron-right"></i>
            </button>
          </div>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="opportunites-search-bar"
            style={{ fontFamily: 'Inter, sans-serif', maxWidth: "120px" }}
          >
            {years.map((year) => (
              <option key={year.value} value={year.value}>
                {year.label}
              </option>
            ))}
          </select>
          <select
            value={selectedOpportunityType}
            onChange={(e) => setSelectedOpportunityType(e.target.value)}
            className="opportunites-search-bar"
            style={{ fontFamily: 'Inter, sans-serif', maxWidth: "200px" }}
          >
            <option value="all">Tous les types de demande</option>
            {opportunityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="opportunites-search-bar"
            style={{ fontFamily: 'Inter, sans-serif', maxWidth: "180px" }}
          >
            <option value="all">Tous les statuts</option>
            <option value="En attente de Paiement">En attente de Paiement</option>
            <option value="Réglée">Réglée</option>
            <option value="Gelée">Gelée</option>
          </select>
          <input
            type="text"
            className="opportunites-search-bar"
            placeholder="Rechercher une facture..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontFamily: 'Inter, sans-serif' }}
          />
        </div>
        
      <div className="factures-table-container" style={{ marginTop: '20px' }}>
        <table className="factures-table" style={{ fontFamily: 'Inter, sans-serif' }}>
          <thead>
            <tr>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('id')}
              >
                Numéro{renderSortIcon2('id')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('companyId')}
              >
                Nom de la société{renderSortIcon2('companyId')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('opportunityType')}
              >
                Type de demande{renderSortIcon2('opportunityType')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('qte')}
              >
                Qté{renderSortIcon2('qte')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('pu')}
              >
                PU (€){renderSortIcon2('pu')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('montantHT')}
              >
                Montant HT(€){renderSortIcon2('montantHT')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('montantTTC')}
              >
                Montant TTC(€){renderSortIcon2('montantTTC')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('date')}
              >
                Date de création{renderSortIcon2('date')}
              </th>
              <th
                style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                onClick={() => handleSort2('status')}
              >
                Statut{renderSortIcon2('status')}
              </th>
              <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Documents</th>
              {userRole !== "foodinnov" && (
                <th style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentRegexportInvoices.length === 0 ? (
              <tr>
                <td colSpan={userRole === "admin" ? 11 : 10} style={{ textAlign: "center", fontFamily: 'Inter, sans-serif' }}>Aucune facture trouvée.</td>
              </tr>
            ) : (
              <>
                {currentRegexportInvoices.map((invoice, index) => (
                  <tr key={`regexport-table-${invoice.uid || (invoice.id + '-' + index)}`}>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.id}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.companyId}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      {(() => {
                        const opps = regexportOpportunities[invoice.companyId] || [];
                        const byDate = invoice.opportunityDate ? opps.find((opp: any) => opp.type === invoice.opportunityType && String(opp.date) === String(invoice.opportunityDate)) : null;
                        const byQtePu = opps.find((opp: any) => opp.type === invoice.opportunityType && Number(opp.qte) === Number(invoice.qte) && Math.abs(Number(opp.pu) - Number(invoice.pu)) < 0.01);
                        const byMontant = opps.find((opp: any) => opp.type === invoice.opportunityType && Math.abs(Number(opp.amount) - Number(invoice.montantHT)) < 0.01);
                        const byType = opps.find((opp: any) => opp.type === invoice.opportunityType);
                        const matchingOpp = byDate || byQtePu || byMontant || byType || null;
                        const matchStep = byDate ? 'date' : byQtePu ? 'qte+pu' : byMontant ? 'montantHT' : byType ? 'type seul' : 'aucun';
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                            {invoice.opportunityType}
                            <Tippy
                              content={
                                <div style={{ fontSize: '0.82rem', textAlign: 'left', padding: '2px 4px', lineHeight: '1.6' }}>
                                  {matchingOpp ? (
                                    <>
                                      <div><strong>Contact :</strong> {matchingOpp.contact || '-'}</div>
                                      <div><strong>Qté :</strong> {matchingOpp.qte ?? '-'}</div>
                                      <div><strong>PU :</strong> {matchingOpp.pu ? `${Number(matchingOpp.pu).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Remise :</strong> {matchingOpp.remise !== undefined ? `${matchingOpp.remise} %` : '-'}</div>
                                      <div><strong>Montant HT :</strong> {matchingOpp.amount ? `${Number(matchingOpp.amount).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Montant TTC :</strong> {matchingOpp.ttc ? `${Number(matchingOpp.ttc).toFixed(2)} €` : '-'}</div>
                                      <div><strong>Date :</strong> {matchingOpp.date ? new Date(matchingOpp.date).toLocaleDateString('fr-FR') : '-'}</div>
                                      <div><strong>Statut :</strong> {matchingOpp.status || '-'}</div>
                                      {matchingOpp.description && <div><strong>Description :</strong> {matchingOpp.description}</div>}
                                    </>
                                  ) : (
                                    <div>Aucune opportunité associée</div>
                                  )}
                                </div>
                              }
                              placement="right"
                              allowHTML={false}
                              interactive={true}
                              onShow={() => {
                                console.group(`[RegExport] (i) — ${invoice.companyId} / ${invoice.opportunityType}`);
                                console.log('--- Facture ---', {
                                  id: invoice.id,
                                  companyId: invoice.companyId,
                                  opportunityType: invoice.opportunityType,
                                  opportunityDate: invoice.opportunityDate,
                                  qte: invoice.qte,
                                  pu: invoice.pu,
                                  montantHT: invoice.montantHT,
                                  montantTTC: invoice.montantTTC,
                                  date: invoice.date?.toDate?.()?.toLocaleDateString('fr-FR'),
                                  uid: invoice.uid,
                                });
                                console.log(`Match utilisé : ${matchStep}`);
                                console.log('--- Opportunité matchée ---', matchingOpp ?? 'aucune');
                                console.log(`--- Toutes les opps (${opps.length}) pour ${invoice.companyId} ---`, opps);
                                console.groupEnd();
                              }}
                            >
                              <i
                                className="bi bi-info-circle"
                                style={{ cursor: 'help', color: matchingOpp ? '#007bff' : '#adb5bd', fontSize: '0.9rem' }}
                              />
                            </Tippy>
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{invoice.qte}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.pu, "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(factureHT(invoice), "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(factureTTC(invoice), "currency")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>{formatValue(invoice.date, "date")}</td>
                    <td style={{ fontFamily: 'Inter, sans-serif' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8rem',
                        fontWeight: '500',
                        backgroundColor: invoice.status === 'Réglée' ? '#d4edda' : invoice.status === 'Gelée' ? '#cfe2ff' : '#f8d7da',
                        color: invoice.status === 'Réglée' ? '#155724' : invoice.status === 'Gelée' ? '#084298' : '#721c24',
                        border: `1px solid ${invoice.status === 'Réglée' ? '#c3e6cb' : invoice.status === 'Gelée' ? '#b6d4fe' : '#f5c6cb'}`
                      }}>
                        {invoice.status === 'Pas Réglée' ? 'En attente de Paiement' : (invoice.status || 'En attente de Paiement')}
                      </span>
                    </td>
                    <td className="document-icon-cell" style={{ fontFamily: 'Inter, sans-serif' }}>
                      <i
                        className="bi bi-filetype-pdf"
                        title="Aperçu PDF"
                        onClick={() => handleGenerateInvoicePdf(invoice)}
                        style={{ cursor: 'pointer', marginRight: '10px', fontSize: '1.2rem', color: '#0d6efd' }}
                      />
                    </td>
                    {userRole !== "foodinnov" && (
                      <td className="action-icons">
                        <i className="bi bi-pencil-square edit-icon" title="Modifier" onClick={() => handleEditRegexportInvoice(invoice)} style={{ color: '#007bff', cursor: 'pointer' }}></i>
                        <i className="bi bi-files copy-icon" title="Dupliquer" style={{ color: '#28a745', cursor: 'pointer', marginLeft: 2 }}></i>
                        <i className="bi bi-trash delete-icon" title="Supprimer" onClick={() => handleDeleteRegexportInvoice(invoice)} style={{ color: '#dc3545', cursor: 'pointer', marginLeft: 2 }}></i>
                      </td>
                    )}
                  </tr>
                ))}
                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>Total</td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}></td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(regexportTotals.totalHT, "currency")}
                  </td>
                  <td style={{ fontFamily: 'Inter, sans-serif' }}>
                    {formatValue(regexportTotals.totalTTC, "currency")}
                  </td>
                  <td colSpan={userRole === "admin" ? 4 : 3}></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
        
        {/* Totaux par statut pour RegExport */}
        <div style={{ marginTop: '20px', display: 'flex', gap: '20px', justifyContent: 'space-around', fontFamily: 'Inter, sans-serif' }}>
          {/* Total Réglées */}
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#d4edda', 
            borderRadius: '5px', 
            border: '1px solid #c3e6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#155724', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-bank"></i>
              Total Réglées
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>HT:</strong> {formatValue(regexportTotalsByStatus.reglee.totalHT, "currency")}</div>
              <div><strong>TTC:</strong> {formatValue(regexportTotalsByStatus.reglee.totalTTC, "currency")}</div>
            </div>
          </div>
          
          {/* Total En attente de Paiement */}
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#f8d7da', 
            borderRadius: '5px', 
            border: '1px solid #f5c6cb',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#721c24', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-wallet2"></i>
              Total En attente de Paiement
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>HT:</strong> {formatValue(regexportTotalsByStatus.enAttente.totalHT, "currency")}</div>
              <div><strong>TTC:</strong> {formatValue(regexportTotalsByStatus.enAttente.totalTTC, "currency")}</div>
            </div>
          </div>

          {/* Total Gelées */}
          <div style={{
            padding: '20px',
            backgroundColor: '#cfe2ff',
            borderRadius: '5px',
            border: '1px solid #b6d4fe',
            flex: 1,
            minWidth: '500px',
            maxWidth: '600px'
          }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#084298', fontWeight: '600', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i className="bi bi-snow"></i>
              Total Gelées
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '1rem' }}>
              <div><strong>HT:</strong> {formatValue(regexportTotalsByStatus.gelee.totalHT, "currency")}</div>
              <div><strong>TTC:</strong> {formatValue(regexportTotalsByStatus.gelee.totalTTC, "currency")}</div>
            </div>
          </div>
        </div>
      </div>
      </section>

      <FactureFoodinnovModal
        show={showModal}
        handleClose={handleCloseModal}
        initialData={selectedInvoice}
        preselectedDevis={preselectedDevis}
      />

      <FactureModal
        show={showRegexportModal}
        handleClose={() => {
          setShowRegexportModal(false);
          setSelectedRegexportInvoice(null);
        }}
        initialData={selectedRegexportInvoice}
      />

      <FacturePreviewModal
        show={showPreviewModal}
        handleClose={handleClosePreviewModal}
        invoiceData={invoiceToPreview}
        pdfData={invoicePdfPreviewUrl}
      />

      <StatusEditModal
        show={showStatusModal}
        handleClose={handleCloseStatusModal}
        invoice={invoiceToEditStatus}
        onStatusUpdated={handleStatusUpdated}
      />

      {invoiceToDelete && (
        <div className="modal-overlay">
          <div className="delete-confirmation">
            <h2>Confirmer la suppression</h2>
            <p>Êtes-vous sûr de vouloir supprimer la facture "{invoiceToDelete.id}" de la société "{invoiceToDelete.companyId}" ?</p>
            <p className="warning-text">Cette action est irréversible.</p>
            <div className="modal-actions">
              <button className="cancel-button" onClick={cancelDeleteInvoice}>
                Annuler
              </button>
              <button className="delete-button" onClick={confirmDeleteInvoice}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {userRole === 'admin' && <BillingRecapTable />}

      <Footer />
    </div>
  );
};

export default FacturesFoodinnov; 